import { Sidebar } from "@iq/models";
import { formatTimeRange } from "../../utils/time";
import { confirmationLabel } from "../../utils/appointmentAction";
import styles from "./AppointmentSidebar.module.css";

function formatPrice(cents) {
    if (typeof cents !== "number") return null;
    return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateKey) {
    if (!dateKey) return "";
    return new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
        .format(new Date(`${dateKey}T12:00:00`));
}

function formatPayment(paymentStatus) {
    if (!paymentStatus) return "unpaid";
    switch (paymentStatus) {
        case "unpaid":
            return "Unpaid";
        case "deposit_paid":
            return "Deposit paid";
        case "paid":
            return "Paid";
        default:
            return paymentStatus;
    }
}

function AppointmentSidebar({
    appointment,
    dateKey,
    action,
    verification,
    isOpen,
    onClose,
    onComplete,
    onFollowUp,
    onConfirm,
    onDelete,
}) {
    if (!appointment) return null;

    const name = appointment.customer?.name || appointment.customer_name || "Appointment";
    const actionButtons = [];

    // Complete and follow-up open modals, so the sidebar closes first.
    if (action === "complete") {
        actionButtons.push({ key: "complete", text: "Complete appointment", onClick: () => onComplete(appointment) });
    } else if (action === "follow-up") {
        actionButtons.push({ key: "follow-up", text: "Book follow-up", onClick: () => onFollowUp(appointment) });
    } else if (action === "confirm") {
        actionButtons.push({ key: "confirm", text: confirmationLabel(appointment), onClick: () => onConfirm(appointment) });
    }
    actionButtons.push({
        key: "delete",
        text: "Delete",
        onClick: () => onDelete(appointment),
        style: { backgroundColor: "var(--danger)", borderColor: "var(--danger)" },
    });

    return (
        <Sidebar
            isOpen={isOpen}
            onClose={onClose}
            title={name}
            description={`${formatDate(dateKey)} · ${formatTimeRange(appointment.start_time, appointment.end_time)}`}
            actionButtons={actionButtons}
        >
            <div className={styles.details} aria-label="Selected appointment details">
                <dl className={styles.detailList}>
                    <div>
                        <dt>Status</dt>
                        <dd>{appointment.status || "pending"}</dd>
                    </div>
                    <div>
                        <dt>Payment</dt>
                        <dd>{formatPayment(appointment.payment_status)}</dd>
                    </div>
                    {appointment.pricing?.name ? (
                        <div><dt>Service</dt><dd>{appointment.pricing.name}</dd></div>
                    ) : null}
                    {appointment.discount_percentage > 0 ? (
                        <div>
                            <dt>Discount</dt>
                            <dd>
                                {appointment.discount_name ? `${appointment.discount_name} · ` : ""}
                                {appointment.discount_percentage}% off applied
                            </dd>
                        </div>
                    ) : null}
                    {formatPrice(appointment.pricing?.total_price) ? (
                        <div><dt>Total</dt><dd>{formatPrice(appointment.pricing.total_price)}</dd></div>
                    ) : null}
                    {appointment.customer?.phone ? (
                        <div><dt>Phone</dt><dd>{appointment.customer.phone}</dd></div>
                    ) : null}
                    {appointment.customer?.email ? (
                        <div><dt>Email</dt><dd>{appointment.customer.email}</dd></div>
                    ) : null}
                </dl>
                <details key={appointment.id ?? appointment.hashed_appointment_id} className={styles.questionnaire}>
                    <summary>Customer questionnaire</summary>
                    {appointment.customer?.questionnaire_answers?.length ? (
                        <dl>
                            {appointment.customer.questionnaire_answers.map((answer, index) => (
                                <div key={`${answer.question_id}-${index}`}>
                                    <dt>{answer.question || `Question ${answer.question_id}`}</dt>
                                    <dd>{answer.answer?.trim() ? answer.answer : "No answer provided"}</dd>
                                </div>
                            ))}
                        </dl>
                    ) : <p>No questionnaire answers on file.</p>}
                </details>
                {verification && verification.AppointmentID === appointment.id ? (
                    <div className={styles.verification} role="status">
                        Verification status: {verification.Status}
                    </div>
                ) : null}
            </div>
        </Sidebar>
    );
}

export default AppointmentSidebar;
